import React, { useState, useEffect } from 'react';
import axios from 'axios';
import constant from '../constants/constant';
import { Users, Search, RefreshCw, Edit2, Clock, Calendar } from 'lucide-react';

const HostManagement = () => {
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedHost, setSelectedHost] = useState(null);
  
  const [overrideForm, setOverrideForm] = useState({
    target_user_id: '',
    duration_mins: 120,
    valid_days: 10
  });

  const [toast, setToast] = useState({ message: '', type: '', visible: false });
  const showToast = (message, type = 'success') => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast({ message: '', type: '', visible: false }), 3500);
  };

  useEffect(() => {
    fetchHosts();
  }, []);

  const fetchHosts = async () => {
    setLoading(true);
    try {
      let res;
      try {
        res = await axios.get(`${constant.API_BASE_URL}/host/center/admin/hosts-list`, {
          headers: { Authorization: sessionStorage.getItem('adminToken') }
        });
      } catch (e) {
        if (e.response && e.response.status === 404) {
          // Fallback if production server hasn't restarted host_center routes yet
          res = await axios.get(`${constant.API_BASE_URL}/agencies/hosts`, {
            headers: { Authorization: sessionStorage.getItem('adminToken') }
          });
        } else {
          throw e;
        }
      }

      if (res && res.data) {
        if (res.data.success && Array.isArray(res.data.data)) {
          setHosts(res.data.data);
        } else if (Array.isArray(res.data)) {
          setHosts(res.data);
        } else if (Array.isArray(res.data.data)) {
          setHosts(res.data.data);
        } else {
          setHosts([]);
        }
      }
    } catch (err) {
      console.error("Error fetching hosts:", err);
      showToast("Failed to fetch hosts: " + (err.response?.data?.message || err.message), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenOverride = (host = null) => {
    if (host) {
      setSelectedHost(host);
      setOverrideForm({
        target_user_id: host.xoxo_id || host.id,
        duration_mins: host.duration_mins > 0 ? host.duration_mins : 120,
        valid_days: host.valid_days > 0 ? host.valid_days : 10
      });
    } else {
      setSelectedHost(null);
      setOverrideForm({
        target_user_id: '',
        duration_mins: 120,
        valid_days: 10
      });
    }
    setIsModalOpen(true);
  };

  const handleOverrideSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${constant.API_BASE_URL}/host/center/admin/override-active-stats`, {
        target_user_id: overrideForm.target_user_id,
        duration_mins: Number(overrideForm.duration_mins),
        valid_days: Number(overrideForm.valid_days)
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });

      if (res.data.success) {
        showToast(res.data.message || 'Host active stats updated successfully!');
        setIsModalOpen(false);
        fetchHosts();
      } else {
        showToast(res.data.message || 'Error updating stats', 'error');
      }
    } catch (err) {
      showToast("Error updating host stats: " + (err.response?.data?.message || err.message), 'error');
    }
  };

  const filteredHosts = hosts.filter(h => 
    (h.xoxo_id && h.xoxo_id.toString().toLowerCase().includes(searchQuery.toLowerCase())) ||
    (h.first_name && h.first_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (h.last_name && h.last_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (h.email && h.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (h.agency_name && h.agency_name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const totalHostSalary = hosts.reduce((acc, h) => acc + (Number(h.host_amount) || 0), 0);
  const totalAgencyComm = hosts.reduce((acc, h) => acc + (Number(h.agency_comm_amount) || 0), 0);
  const totalBDComm = hosts.reduce((acc, h) => acc + (Number(h.bd_comm_amount) || 0), 0);

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800">Host Management & Salary Calculations</h2>
          <p className="text-slate-500 mt-1">Monitor all active hosts, view live monthly progress, calculated salaries, commissions, and override active stats by XOXO ID.</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={fetchHosts} 
            className="flex items-center gap-2 bg-white text-slate-700 px-5 py-2.5 rounded-xl font-semibold shadow-sm border border-gray-200 hover:bg-gray-50 transition"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button 
            onClick={() => handleOpenOverride()} 
            className="flex items-center gap-2 bg-pink-600 text-white px-5 py-2.5 rounded-xl font-semibold shadow-lg shadow-pink-500/30 hover:bg-pink-700 transition"
          >
            <Edit2 size={18} /> Override Host Stats
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-pink-50 text-pink-600 flex items-center justify-center font-bold text-xl">💎</div>
          <div>
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Host Salary Pool</div>
            <div className="text-xl font-extrabold text-slate-800">💎 {totalHostSalary.toLocaleString()}</div>
            <div className="text-[11px] font-semibold text-slate-400">70% Instant / 30% Hold Split</div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-green-50 text-green-600 flex items-center justify-center font-bold text-xl">🏢</div>
          <div>
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Agency Comm Pool</div>
            <div className="text-xl font-extrabold text-slate-800">💎 {totalAgencyComm.toLocaleString()}</div>
            <div className="text-[11px] font-semibold text-slate-400">100% (10+ Days) / 70% (&lt;10 Days)</div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-xl">💼</div>
          <div>
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">BD Comm Pool</div>
            <div className="text-xl font-extrabold text-slate-800">💎 {totalBDComm.toLocaleString()}</div>
            <div className="text-[11px] font-semibold text-slate-400">22% of Agency Payout</div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center font-bold text-xl">👥</div>
          <div>
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Active Hosts</div>
            <div className="text-xl font-extrabold text-slate-800">{hosts.length} Hosts</div>
            <div className="text-[11px] font-semibold text-slate-400">Monthly Registered Hosts</div>
          </div>
        </div>
      </div>

      {/* Filter / Search Bar */}
      <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-3">
        <Search className="text-slate-400" size={20} />
        <input 
          type="text" 
          placeholder="Search by XOXO ID, Name, Email, or Agency..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full bg-transparent border-none outline-none text-slate-700 placeholder-slate-400 text-sm font-medium"
        />
      </div>

      {/* Hosts Table */}
      <div className="bg-white rounded-[24px] shadow-xl shadow-slate-100/30 border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-20 text-center text-slate-500">Loading Hosts Data...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                  <th className="p-6">Host / XOXO ID</th>
                  <th className="p-6">Agency</th>
                  <th className="p-6">Achieved Coins</th>
                  <th className="p-6">Target Level</th>
                  <th className="p-6">Calculated Host Salary</th>
                  <th className="p-6">Agency Commission</th>
                  <th className="p-6">BD Comm</th>
                  <th className="p-6">Active Days / Mins</th>
                  <th className="p-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filteredHosts.map(host => {
                  const hostAmt = Number(host.host_amount || 0);
                  const instantAmt = Number(host.instant_amount || Math.floor(hostAmt * 0.70));
                  const lockedAmt = Number(host.locked_amount || (hostAmt - instantAmt));
                  const agencyAmt = Number(host.agency_comm_amount || 0);
                  const bdAmt = Number(host.bd_comm_amount || 0);
                  const validDays = Number(host.valid_days || 0);

                  return (
                    <tr key={host.id} className="hover:bg-slate-50/50 transition">
                      <td className="p-6">
                        <div className="flex items-center gap-3">
                          <img 
                            src={host.avatar_url || 'https://via.placeholder.com/150'} 
                            alt="Avatar" 
                            className="w-10 h-10 rounded-full object-cover border border-slate-200"
                          />
                          <div>
                            <div className="font-bold text-slate-800">{host.first_name || 'Host'} {host.last_name || ''}</div>
                            <div className="text-xs font-extrabold text-pink-600">XOXO ID: {host.xoxo_id || host.user_id || 'N/A'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-6 text-slate-600 font-semibold">{host.agency_name || 'Independent'}</td>
                      <td className="p-6 font-extrabold text-slate-700">{Number(host.achieved_coins).toLocaleString()}</td>
                      <td className="p-6">
                        <span className="px-3 py-1 bg-purple-50 text-purple-700 font-extrabold text-xs rounded-full border border-purple-100">
                          Level {host.current_level}
                        </span>
                      </td>
                      <td className="p-6">
                        <div className="font-extrabold text-pink-600 text-base">💎 {hostAmt.toLocaleString()}</div>
                        <div className="text-[11px] font-semibold text-slate-500 mt-0.5">
                          <span className="text-green-600">Instant: 💎{instantAmt.toLocaleString()}</span> • <span className="text-amber-600">10-Day Hold: 💎{lockedAmt.toLocaleString()}</span>
                        </div>
                      </td>
                      <td className="p-6">
                        <div className="font-extrabold text-green-700 text-base">💎 {agencyAmt.toLocaleString()}</div>
                        <div className="mt-0.5">
                          {validDays >= 10 ? (
                            <span className="px-2 py-0.5 bg-green-100 text-green-800 text-[10px] font-bold rounded">100% Paid</span>
                          ) : (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-bold rounded">70% Paid (Penalty)</span>
                          )}
                        </div>
                      </td>
                      <td className="p-6">
                        <div className="font-bold text-blue-600">💎 {bdAmt.toLocaleString()}</div>
                        <div className="text-[10px] font-medium text-slate-400">22% of Agency</div>
                      </td>
                      <td className="p-6">
                        <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-extrabold ${validDays >= 10 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          <Calendar size={14} /> {validDays} / 10 Days
                        </div>
                        <div className="text-xs text-slate-500 font-medium mt-1 flex items-center gap-1">
                          <Clock size={12} className="text-blue-500" /> {host.duration_mins} mins
                        </div>
                      </td>
                      <td className="p-6 text-right">
                        <button 
                          onClick={() => handleOpenOverride(host)} 
                          className="px-4 py-2 bg-slate-900 text-white rounded-xl font-bold text-xs hover:bg-slate-800 transition shadow-sm"
                        >
                          Override Stats
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filteredHosts.length === 0 && (
                  <tr>
                    <td colSpan="9" className="p-10 text-center text-slate-500">No hosts found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Override Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-lg overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-800">
                {selectedHost ? `Override Stats: ${selectedHost.first_name}` : 'Override Host Active Stats'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:bg-slate-50 p-1 rounded-lg">✕</button>
            </div>
            <form onSubmit={handleOverrideSubmit} className="p-6 space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-500 uppercase">Host XOXO ID (Or User ID)</label>
                <input 
                  type="text" 
                  value={overrideForm.target_user_id} 
                  onChange={(e) => setOverrideForm({...overrideForm, target_user_id: e.target.value})}
                  placeholder="Enter XOXO ID (e.g. 100234)"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-semibold text-slate-800" 
                  required 
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Active Mins / Day</label>
                  <input 
                    type="number" 
                    value={overrideForm.duration_mins} 
                    onChange={(e) => setOverrideForm({...overrideForm, duration_mins: e.target.value})}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-semibold text-slate-800" 
                    required 
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Valid Active Days</label>
                  <input 
                    type="number" 
                    value={overrideForm.valid_days} 
                    onChange={(e) => setOverrideForm({...overrideForm, valid_days: e.target.value})}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-semibold text-slate-800" 
                    required 
                  />
                </div>
              </div>

              <div className="p-4 bg-amber-50 rounded-xl border border-amber-100 text-xs text-amber-800 font-medium">
                <strong>Note:</strong> Setting Valid Active Days to 10 will mark the host as eligible for full 100% agency commission release upon holding period completion.
              </div>

              <div className="pt-4 flex justify-end gap-3">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-6 py-2.5 text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition">Cancel</button>
                <button type="submit" className="px-6 py-2.5 bg-pink-600 text-white rounded-xl font-bold hover:bg-pink-700 transition shadow-lg shadow-pink-500/30">Apply Override</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast.visible && (
        <div className={`fixed bottom-4 right-4 z-[9999] px-6 py-3 rounded-xl shadow-lg border text-sm font-bold flex items-center gap-2 animate-in slide-in-from-bottom-5 fade-in duration-300 ${toast.type === 'error' ? 'bg-red-50 text-red-600 border-red-100' : 'bg-green-50 text-green-700 border-green-100'}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default HostManagement;
